import { useState, useCallback } from 'react';
import {
  Campaign,
  CampaignDetail,
  CampaignCreateRequest,
  CampaignAddContactsRequest
} from '../types';
import { withAuthRetry } from '../utils/withAuthRetry';
import {
  CampaignRestartRequest,
  CampaignRunResult,
  fetchCampaigns as fetchCampaignsApi,
  fetchCampaign as fetchCampaignApi,
  createCampaign as createCampaignApi,
  startCampaign as startCampaignApi,
  pauseCampaign as pauseCampaignApi,
  resumeCampaign as resumeCampaignApi,
  completeCampaign as completeCampaignApi,
  restartCampaign as restartCampaignApi,
  addCampaignContacts as addCampaignContactsApi,
  runCampaignById as runCampaignByIdApi,
  deleteCampaign as deleteCampaignApi
} from '../services/hsm.service';

interface UseHsmCampaignsProps {
  accessToken?: string;
  refreshToken?: string;
  attemptRefresh: () => Promise<boolean>;
  canManageHsm: boolean;
}

export interface UseHsmCampaignsReturn {
  // Campaigns state
  campaigns: Campaign[];
  campaignsLoading: boolean;
  selectedCampaign: CampaignDetail | null;
  campaignDetailLoading: boolean;

  // Handlers
  fetchCampaignsData: () => Promise<{ success: boolean; error?: string }>;
  fetchCampaignDetail: (campaignId: number) => Promise<{ success: boolean; error?: string }>;
  handleCreateCampaign: (data: CampaignCreateRequest) => Promise<{ success: boolean; error?: string }>;
  handleStartCampaign: (campaignId: number) => Promise<{ success: boolean; error?: string }>;
  handlePauseCampaign: (campaignId: number) => Promise<{ success: boolean; error?: string }>;
  handleResumeCampaign: (campaignId: number) => Promise<{ success: boolean; error?: string }>;
  handleCompleteCampaign: (campaignId: number) => Promise<{ success: boolean; error?: string }>;
  handleRestartCampaign: (campaignId: number, data: CampaignRestartRequest) => Promise<{ success: boolean; error?: string; contactsReset?: number }>;
  handleAddCampaignContacts: (campaignId: number, data: CampaignAddContactsRequest) => Promise<{ success: boolean; error?: string }>;
  handleRunCampaign: (campaignId: number) => Promise<{ success: boolean; error?: string; result?: CampaignRunResult }>;
  handleDeleteCampaign: (campaignId: number) => Promise<{ success: boolean; error?: string }>;
  clearSelectedCampaign: () => void;
}

export function useHsmCampaigns({
  accessToken,
  attemptRefresh,
  canManageHsm
}: UseHsmCampaignsProps): UseHsmCampaignsReturn {
  // State
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<CampaignDetail | null>(null);
  const [campaignDetailLoading, setCampaignDetailLoading] = useState(false);

  // Fetch all campaigns
  const fetchCampaignsData = useCallback(async (): Promise<{ success: boolean; error?: string }> => {
    if (!canManageHsm || !accessToken) {
      return { success: false, error: 'Acesso negado' };
    }

    setCampaignsLoading(true);
    try {
      const data = await withAuthRetry(
        () => fetchCampaignsApi(accessToken!),
        attemptRefresh
      );
      setCampaigns(data);
      return { success: true };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao carregar campanhas' };
    } finally {
      setCampaignsLoading(false);
    }
  }, [accessToken, attemptRefresh, canManageHsm]);

  // Fetch campaign detail
  const fetchCampaignDetail = useCallback(async (campaignId: number): Promise<{ success: boolean; error?: string }> => {
    if (!canManageHsm || !accessToken) {
      return { success: false, error: 'Acesso negado' };
    }

    setCampaignDetailLoading(true);
    try {
      const data = await withAuthRetry(
        () => fetchCampaignApi(campaignId, accessToken!),
        attemptRefresh
      );
      setSelectedCampaign(data);
      return { success: true };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao carregar detalhes da campanha' };
    } finally {
      setCampaignDetailLoading(false);
    }
  }, [accessToken, attemptRefresh, canManageHsm]);

  // Create campaign
  const handleCreateCampaign = useCallback(async (data: CampaignCreateRequest): Promise<{ success: boolean; error?: string }> => {
    if (!canManageHsm || !accessToken) {
      return { success: false, error: 'Acesso negado' };
    }

    try {
      await withAuthRetry(
        () => createCampaignApi(data, accessToken!),
        attemptRefresh
      );
      await fetchCampaignsData();
      return { success: true };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao criar campanha' };
    }
  }, [accessToken, attemptRefresh, canManageHsm, fetchCampaignsData]);

  // Start campaign
  const handleStartCampaign = useCallback(async (campaignId: number): Promise<{ success: boolean; error?: string }> => {
    if (!canManageHsm || !accessToken) {
      return { success: false, error: 'Acesso negado' };
    }

    try {
      await withAuthRetry(
        () => startCampaignApi(campaignId, accessToken!),
        attemptRefresh
      );
      await fetchCampaignsData();
      return { success: true };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao iniciar campanha' };
    }
  }, [accessToken, attemptRefresh, canManageHsm, fetchCampaignsData]);

  // Pause campaign
  const handlePauseCampaign = useCallback(async (campaignId: number): Promise<{ success: boolean; error?: string }> => {
    if (!canManageHsm || !accessToken) {
      return { success: false, error: 'Acesso negado' };
    }

    try {
      await withAuthRetry(
        () => pauseCampaignApi(campaignId, accessToken!),
        attemptRefresh
      );
      await fetchCampaignsData();
      return { success: true };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao pausar campanha' };
    }
  }, [accessToken, attemptRefresh, canManageHsm, fetchCampaignsData]);

  // Resume campaign
  const handleResumeCampaign = useCallback(async (campaignId: number): Promise<{ success: boolean; error?: string }> => {
    if (!canManageHsm || !accessToken) {
      return { success: false, error: 'Acesso negado' };
    }

    try {
      await withAuthRetry(
        () => resumeCampaignApi(campaignId, accessToken!),
        attemptRefresh
      );
      await fetchCampaignsData();
      return { success: true };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao retomar campanha' };
    }
  }, [accessToken, attemptRefresh, canManageHsm, fetchCampaignsData]);

  // Complete campaign
  const handleCompleteCampaign = useCallback(async (campaignId: number): Promise<{ success: boolean; error?: string }> => {
    if (!canManageHsm || !accessToken) {
      return { success: false, error: 'Acesso negado' };
    }

    try {
      await withAuthRetry(
        () => completeCampaignApi(campaignId, accessToken!),
        attemptRefresh
      );
      await fetchCampaignsData();
      return { success: true };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao completar campanha' };
    }
  }, [accessToken, attemptRefresh, canManageHsm, fetchCampaignsData]);

  // Restart campaign
  const handleRestartCampaign = useCallback(async (campaignId: number, data: CampaignRestartRequest): Promise<{ success: boolean; error?: string; contactsReset?: number }> => {
    if (!canManageHsm || !accessToken) {
      return { success: false, error: 'Acesso negado' };
    }

    try {
      const result = await withAuthRetry(
        () => restartCampaignApi(campaignId, data, accessToken!),
        attemptRefresh
      );
      await fetchCampaignsData();
      return { success: true, contactsReset: result.contactsReset };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao reiniciar campanha' };
    }
  }, [accessToken, attemptRefresh, canManageHsm, fetchCampaignsData]);

  // Add contacts to campaign
  const handleAddCampaignContacts = useCallback(async (campaignId: number, data: CampaignAddContactsRequest): Promise<{ success: boolean; error?: string }> => {
    if (!canManageHsm || !accessToken) {
      return { success: false, error: 'Acesso negado' };
    }

    try {
      await withAuthRetry(
        () => addCampaignContactsApi(campaignId, data, accessToken!),
        attemptRefresh
      );
      // Refresh campaign detail to show updated contacts
      if (selectedCampaign?.id === campaignId) {
        await fetchCampaignDetail(campaignId);
      }
      return { success: true };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao adicionar contatos a campanha' };
    }
  }, [accessToken, attemptRefresh, canManageHsm, selectedCampaign?.id, fetchCampaignDetail]);

  // Run campaign by ID (with forceResend=true for testing)
  const handleRunCampaign = useCallback(async (campaignId: number): Promise<{ success: boolean; error?: string; result?: CampaignRunResult }> => {
    if (!canManageHsm || !accessToken) {
      return { success: false, error: 'Acesso negado' };
    }

    try {
      const result = await withAuthRetry(
        () => runCampaignByIdApi(campaignId, accessToken!, true),
        attemptRefresh
      );
      // Refresh campaign detail to show updated stats
      if (selectedCampaign?.id === campaignId) {
        await fetchCampaignDetail(campaignId);
      }
      await fetchCampaignsData();
      return { success: true, result };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao executar campanha' };
    }
  }, [accessToken, attemptRefresh, canManageHsm, selectedCampaign?.id, fetchCampaignDetail, fetchCampaignsData]);

  // Delete campaign (only DRAFT status allowed)
  const handleDeleteCampaign = useCallback(async (campaignId: number): Promise<{ success: boolean; error?: string }> => {
    if (!canManageHsm || !accessToken) {
      return { success: false, error: 'Acesso negado' };
    }

    try {
      await withAuthRetry(
        () => deleteCampaignApi(campaignId, accessToken!),
        attemptRefresh
      );
      await fetchCampaignsData();
      // Clear selected campaign if it was the deleted one
      if (selectedCampaign?.id === campaignId) {
        setSelectedCampaign(null);
      }
      return { success: true };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao excluir campanha' };
    }
  }, [accessToken, attemptRefresh, canManageHsm, fetchCampaignsData, selectedCampaign?.id]);

  // Clear selected campaign
  const clearSelectedCampaign = useCallback(() => {
    setSelectedCampaign(null);
  }, []);

  return {
    campaigns,
    campaignsLoading,
    selectedCampaign,
    campaignDetailLoading,
    fetchCampaignsData,
    fetchCampaignDetail,
    handleCreateCampaign,
    handleStartCampaign,
    handlePauseCampaign,
    handleResumeCampaign,
    handleCompleteCampaign,
    handleRestartCampaign,
    handleAddCampaignContacts,
    handleRunCampaign,
    handleDeleteCampaign,
    clearSelectedCampaign
  };
}
